import React from 'react';

const ProgressTab = ({ user, missions = [], lessons = [], quizzes = [], tabId = "progress", tabLabel = "Progress" }) => {
  // Calculate comprehensive progress statistics (missions + lessons + quizzes)
  const totalMissions = missions.length;
  const completedMissions = missions.filter(mission => mission.status === 'completed').length;
  const inProgressMissions = missions.filter(mission => mission.status === 'in-progress').length;
  const assignedMissions = missions.filter(mission => mission.status === 'assigned').length;
  
  const totalLessons = lessons.length;
  const completedLessons = lessons.filter(lesson => lesson.status === 'completed').length;
  const inProgressLessons = lessons.filter(lesson => lesson.status === 'in-progress').length;
  const assignedLessons = lessons.filter(lesson => lesson.status === 'assigned').length;
  
  const totalQuizzes = quizzes.length;
  const completedQuizzes = quizzes.filter(quiz => quiz.assignmentStatus === 'completed' || quiz.assignmentStatus === 'graded').length;
  const inProgressQuizzes = quizzes.filter(quiz => quiz.assignmentStatus === 'in-progress').length;
  const assignedQuizzes = quizzes.filter(quiz => quiz.assignmentStatus === 'assigned').length;
  
  // Calculate comprehensive totals
  const totalAssignments = totalMissions + totalLessons + totalQuizzes;
  const totalCompleted = completedMissions + completedLessons + completedQuizzes;
  const totalInProgress = inProgressMissions + inProgressLessons + inProgressQuizzes;
  const totalAssigned = assignedMissions + assignedLessons + assignedQuizzes;
  
  // Calculate overall progress percentage
  const overallProgress = totalAssignments > 0 ? Math.round((totalCompleted / totalAssignments) * 100) : 0;
  
  // Calculate quiz performance statistics
  const quizScores = quizzes
    .filter(quiz => quiz.percentage !== undefined && quiz.percentage !== null)
    .map(quiz => quiz.percentage);
  const averageQuizScore = quizScores.length > 0 
    ? Math.round(quizScores.reduce((sum, score) => sum + score, 0) / quizScores.length)
    : 0;
  const totalQuizPoints = quizzes.reduce((sum, quiz) => sum + (quiz.totalPoints || 0), 0);
  const earnedQuizPoints = quizzes.reduce((sum, quiz) => {
    if (quiz.percentage !== undefined && quiz.percentage !== null) {
      return sum + Math.round((quiz.totalPoints || 0) * (quiz.percentage / 100));
    }
    return sum;
  }, 0);

  // Calculate mastery level based on completed assignments (missions + lessons + quizzes)
  const getMasteryLevel = () => {
    if (totalCompleted >= 15) return { level: 'Master', color: '#28a745', icon: '🏆' };
    if (totalCompleted >= 10) return { level: 'Expert', color: '#17a2b8', icon: '⭐' };
    if (totalCompleted >= 6) return { level: 'Learning', color: '#ffc107', icon: '📚' };
    if (totalCompleted >= 2) return { level: 'Starting', color: '#fd7e14', icon: '🌱' };
    return { level: 'Not Started', color: '#6c757d', icon: '🎯' };
  };
  
  const mastery = getMasteryLevel();

  return (
    <div tabId={tabId} tabLabel={tabLabel} className="progress-tab-content">
      <div className="row">
        {/* Overall Progress Card */}
        <div className="col-md-6 mb-4">
          <div className="card h-100">
            <div className="card-body text-center">
              <h5 className="card-title">Overall Assignment Progress</h5>
              <div className="mb-3">
                <div className="progress" style={{ height: '20px' }}>
                  <div 
                    className="progress-bar" 
                    style={{ 
                      width: `${overallProgress}%`,
                      backgroundColor: mastery.color
                    }}
                  >
                    {overallProgress}%
                  </div>
                </div>
              </div>
              <p className="mb-2">
                <strong>{totalCompleted}</strong> of <strong>{totalAssignments}</strong> assignments completed
              </p>
              <div className="badge" style={{ 
                backgroundColor: mastery.color, 
                color: 'white',
                fontSize: '1rem',
                padding: '8px 16px'
              }}>
                {mastery.icon} {mastery.level}
              </div>
            </div>
          </div>
        </div>
        
        {/* Assignment Status Breakdown */}
        <div className="col-md-6 mb-4">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title">Assignment Status</h5>
              <div className="row text-center">
                <div className="col-4">
                  <div className="border rounded p-3 mb-2" style={{ backgroundColor: '#e3f2fd' }}>
                    <h4 className="text-primary mb-1">{totalAssigned}</h4>
                    <small className="text-muted">Assigned</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="border rounded p-3 mb-2" style={{ backgroundColor: '#fff3e0' }}>
                    <h4 className="text-warning mb-1">{totalInProgress}</h4>
                    <small className="text-muted">In Progress</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="border rounded p-3 mb-2" style={{ backgroundColor: '#e8f5e8' }}>
                    <h4 className="text-success mb-1">{totalCompleted}</h4>
                    <small className="text-muted">Completed</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Breakdown by Type */}
      <div className="row mb-4">
        <div className="col-md-4 mb-3">
          <div className="card h-100">
            <div className="card-body text-center">
              <h6 className="card-title text-primary">🚀 Missions</h6>
              <div className="progress mb-2" style={{ height: '15px' }}>
                <div 
                  className="progress-bar bg-primary" 
                  style={{ width: `${totalMissions > 0 ? Math.round((completedMissions / totalMissions) * 100) : 0}%` }}
                >
                  {totalMissions > 0 ? Math.round((completedMissions / totalMissions) * 100) : 0}%
                </div>
              </div>
              <p className="mb-1">
                <strong>{completedMissions}</strong> of <strong>{totalMissions}</strong> completed
              </p>
              <small className="text-muted">
                {inProgressMissions} in progress, {assignedMissions} assigned
              </small>
            </div>
          </div>
        </div>
        
        <div className="col-md-4 mb-3">
          <div className="card h-100">
            <div className="card-body text-center">
              <h6 className="card-title text-success">📚 Lessons</h6>
              <div className="progress mb-2" style={{ height: '15px' }}>
                <div 
                  className="progress-bar bg-success" 
                  style={{ width: `${totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0}%` }}
                >
                  {totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0}%
                </div>
              </div>
              <p className="mb-1">
                <strong>{completedLessons}</strong> of <strong>{totalLessons}</strong> completed
              </p>
              <small className="text-muted">
                {inProgressLessons} in progress, {assignedLessons} assigned
              </small>
            </div>
          </div>
        </div>
        
        <div className="col-md-4 mb-3">
          <div className="card h-100">
            <div className="card-body text-center">
              <h6 className="card-title text-purple">❓ Quizzes</h6>
              <div className="progress mb-2" style={{ height: '15px' }}>
                <div 
                  className="progress-bar" 
                  style={{ 
                    width: `${totalQuizzes > 0 ? Math.round((completedQuizzes / totalQuizzes) * 100) : 0}%`,
                    backgroundColor: '#7f14c7'
                  }}
                >
                  {totalQuizzes > 0 ? Math.round((completedQuizzes / totalQuizzes) * 100) : 0}%
                </div>
              </div>
              <p className="mb-1">
                <strong>{completedQuizzes}</strong> of <strong>{totalQuizzes}</strong> completed
              </p>
              <small className="text-muted">
                {inProgressQuizzes} in progress, {assignedQuizzes} assigned
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Quiz Performance Section */}
      {totalQuizzes > 0 && (
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">📊 Quiz Performance</h5>
                <div className="row text-center">
                  <div className="col-md-3">
                    <div className="p-3">
                      <i className="fas fa-chart-line fa-2x text-purple mb-2"></i>
                      <h6>Average Score</h6>
                      <h4 className="text-purple">{averageQuizScore}%</h4>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="p-3">
                      <i className="fas fa-star fa-2x text-warning mb-2"></i>
                      <h6>Points Earned</h6>
                      <h4 className="text-warning">{earnedQuizPoints}</h4>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="p-3">
                      <i className="fas fa-trophy fa-2x text-success mb-2"></i>
                      <h6>Total Points</h6>
                      <h4 className="text-success">{totalQuizPoints}</h4>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="p-3">
                      <i className="fas fa-percentage fa-2x text-info mb-2"></i>
                      <h6>Success Rate</h6>
                      <h4 className="text-info">
                        {totalQuizPoints > 0 ? Math.round((earnedQuizPoints / totalQuizPoints) * 100) : 0}%
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Student Stats */}
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Your Learning Journey</h5>
              <div className="row">
                <div className="col-md-2 text-center">
                  <div className="p-3">
                    <i className="fas fa-star fa-2x text-warning mb-2"></i>
                    <h6>Total Stars</h6>
                    <h4 className="text-warning">{user?.totalStars || 0}</h4>
                  </div>
                </div>
                <div className="col-md-2 text-center">
                  <div className="p-3">
                    <i className="fas fa-rocket fa-2x text-primary mb-2"></i>
                    <h6>Missions</h6>
                    <h4 className="text-primary">{completedMissions}</h4>
                    <small className="text-muted">completed</small>
                  </div>
                </div>
                <div className="col-md-2 text-center">
                  <div className="p-3">
                    <i className="fas fa-book fa-2x text-success mb-2"></i>
                    <h6>Lessons</h6>
                    <h4 className="text-success">{completedLessons}</h4>
                    <small className="text-muted">completed</small>
                  </div>
                </div>
                <div className="col-md-2 text-center">
                  <div className="p-3">
                    <i className="fas fa-question-circle fa-2x text-purple mb-2"></i>
                    <h6>Quizzes</h6>
                    <h4 className="text-purple">{completedQuizzes}</h4>
                    <small className="text-muted">completed</small>
                  </div>
                </div>
                <div className="col-md-2 text-center">
                  <div className="p-3">
                    <i className="fas fa-clock fa-2x text-info mb-2"></i>
                    <h6>In Progress</h6>
                    <h4 className="text-info">{totalInProgress}</h4>
                    <small className="text-muted">activities</small>
                  </div>
                </div>
                <div className="col-md-2 text-center">
                  <div className="p-3">
                    <i className="fas fa-target fa-2x text-warning mb-2"></i>
                    <h6>Ready to Start</h6>
                    <h4 className="text-warning">{totalAssigned}</h4>
                    <small className="text-muted">activities</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressTab; 